import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Typography,
  alpha,
  styled,
} from "@mui/material";

import { themePalette } from "../../../theme";

export const StyledCard = styled(Card, {
  shouldForwardProp: (prop) =>
    prop !== "isSelected" &&
    prop !== "isHighlighted" &&
    prop !== "isPreferred" &&
    prop !== "clickable" &&
    prop !== "compact" &&
    prop !== "showBorder",
})<{
  isSelected?: boolean;
  isHighlighted?: boolean;
  isPreferred?: boolean;
  clickable?: boolean;
  compact?: boolean;
  showBorder?: boolean;
}>`
  margin-bottom: ${({ compact }) => (compact ? "8px" : "12px")};
  border-radius: 8px;
  border: ${({ showBorder }) =>
    showBorder ? `1px solid ${themePalette.neutral200}` : "none"};
  box-shadow: ${themePalette.boxShadow};
  overflow: hidden;
  ${({ clickable }) => clickable && `cursor: pointer;`}

  ${({ isPreferred }) =>
    isPreferred &&
    `
      border: 1px solid ${themePalette.tealAccent};
      background-color: ${themePalette.blueSubtle};
    `}

  ${({ isSelected, showBorder, theme }) =>
    isSelected &&
    (showBorder
      ? `border: 2px solid ${theme.palette.custom.redMedium};`
      : `
          outline: 2px solid ${theme.palette.custom.redMedium};
          outline-offset: -2px;
        `)}

  ${({ isHighlighted, isPreferred }) => {
    if (!isHighlighted) return "";
    if (isPreferred) {
      return `
        border: 2px solid ${themePalette.tealAccent};
        outline: none;
      `;
    }
    return `
      border: 1px solid ${themePalette.greyLight};
    `;
  }}

  ${({ clickable }) =>
    clickable &&
    `
    &:hover {
      box-shadow: 0px 6px 11px 0px rgba(0, 0, 0, 0.08);
    }
  `}

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const StyledCardContent = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== "compact",
})<{ compact?: boolean }>`
  padding: ${({ compact }) => (compact ? "14px 16px 0" : "20px 20px 0")};

  &:last-child {
    padding-bottom: ${({ compact }) => (compact ? "14px" : "20px")};
  }
`;

export const StyledCardActions = styled(CardActions, {
  shouldForwardProp: (prop) => prop !== "compact",
})<{ compact?: boolean }>`
  padding: 0 ${({ compact }) => (compact ? "16px" : "20px")}
    ${({ compact }) => (compact ? "14px" : "20px")};
  display: block;
`;

export const OutlineIconActionButton = styled(Button)`
  min-width: 45px;
  width: 45px;
  height: 45px;
  padding: 0;
  border-radius: 22.5px;
  background-color: ${({ theme }) => theme.palette.common.white};

  &:hover {
    border-width: 2px;
    background-color: ${({ theme }) =>
      alpha(theme.palette.custom.redMedium, 0.04)};
  }

  & svg {
    width: 20px;
    height: 20px;
  }
`;

const PrimaryActionButton = styled(Button)`
  min-width: auto;
  height: 45px;
  padding-left: 15px;
  padding-right: 20px;
  border-radius: 22.5px;
  gap: 10px;
  font-size: 21px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.02em;

  & .MuiButton-startIcon {
    margin: 0;
  }

  & svg {
    width: 20px;
    height: 20px;
  }
`;

export const PhlebotomyCardActionsRow = styled(Box)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  justify-content: flex-end;
  gap: 20px;
  padding-top: 8px;
  padding-bottom: 8px;
`;

export const PhlebotomyPrimaryActionButton = styled(PrimaryActionButton)`
  min-width: 114px;
  justify-content: center;
  font-size: 20px;
  text-transform: uppercase;

  &:only-child {
    margin-left: auto;
  }
`;

export const LocationName = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: ${themePalette.greyMedium};
  flex: 1;
  min-width: 0;
`;

export const LocationTitleRow = styled(Box)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

export const LocationTypeRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const LocationPhone = styled(Typography)`
  font-size: 18px;
  line-height: 28px;
  color: ${themePalette.greyMedium};
`;

export const LocationHours = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 16px;
  padding-bottom: 16px;
`;

export const LocationHourLine = styled(Typography)`
  font-size: 14px;
  line-height: 20px;
  color: ${themePalette.greyMedium};
`;

export const LocationHourValue = styled("span")`
  font-weight: 700;
`;
